export type StaffingStatus = "POOL" | "BEACH" | "ASSIGNED" | "HELD";

export interface Person {
  email: string;
  name: string;
  region?: string;
  status?: StaffingStatus;
  speciality?: string;
  coe_level?: string;
  notes?: string;
  availability_start?: string;
  availability_end?: string;
  skills?: string[];
  certifications?: string[];
  roles?: string[];
  onboarding_status?: {
    onboarded?: boolean;
    sb_license?: boolean;
    psa_profile?: boolean;
  };
  created_at?: string;
  updated_at?: string;
}

export interface RoleDefinition {
  role_code: string;
  twks_role: string;
  aws_role: string;
  aws_level: string;
  label: string;
}

export interface Archetype {
  name: string;
}

export interface Skill {
  skill_code: string;
  name: string;
  description?: string;
  group_type?: string;
  group_code?: string;
  tags?: string[];
  certs?: string[];
  related_skills?: string[];
  aliases?: string[];
  last_reviewed_utc?: string;
}

export interface Opportunity {
  opportunity_id: string;
  name: string;
  customer_legal_name?: string;
  due_date?: string;
  created_at?: string;
  updated_at?: string;
}

export interface FeatureFlags {
  // Top-level routes
  dashboard: boolean;
  myDashboard: boolean;
  jobBoard: boolean;
  reports: boolean;
  admin: boolean;

  // Dashboard tabs
  people: boolean;
  beach: boolean;
  awsRrs: boolean;
  skills: boolean;
  assignments: boolean;
  opportunities: boolean;
  rrMatcher: boolean;
}

export interface Project {
  project_id: string;
  title: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  created_at?: string;
  updated_at?: string;
  delivery_unit?: string;
  estimated_skills?: string[];
  notes?: string;
  description?: string;
  aws_role?: string;
  aws_grade?: string;
  tw_role?: string;
  tw_grade?: string;
  tw_archetype?: string;
  fte_allocation?: string;
  staffing_status?: string;
  submitted_to_aws?: boolean;
  outcome?: string;
  proposed_candidates?: string[];
  week_start?: string;
  summary?: AiProjectSummary;
  requested_hours?: number | null;
  avg_week_hours?: number | null;
  opportunity_id?: string;
}

export interface Assignment {
  assignment_id: string;
  project_id: string;
  person_email: string;
  status: string;
  start_date?: string;
  end_date?: string;
  allocation?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export type DynamoAttributeValue =
  | { S: string }
  | { BOOL: boolean }
  | { L: DynamoAttributeValue[] };

export interface AiProjectSummary {
  project_id: string;
  title: string;
  status: string;
  start_date: string;
  end_date: string;
  week_start: string;
  delivery_unit: string;
  aws_role: string;
  aws_grade: string;
  tw_role: string;
  tw_grade: string;
  tw_archetype: string;
  fte_allocation: string;
  staffing_status: string;
  submitted_to_aws: boolean;
  outcome: string;
  proposed_candidates: string[];
  estimated_skills: string[];
  notes: string;
  requested_hours: number | null;
  avg_week_hours: number | null;
  created_at: string;
  updated_at: string;
}

export interface AiNormalizedProject {
  summary: AiProjectSummary;
  ddb_item: Record<string, DynamoAttributeValue>;
}

export interface AiSnapshotRecord {
  project_id: string;
  title?: string;
  status?: string;
  delivery_unit?: string;
  notes?: string;
  raw_fields?: Record<string, string>;
}

export interface MissingProjectSummary {
  project_id: string;
  title?: string;
  status?: string;
}

export interface AiSnapshotDiffResponse {
  records: AiSnapshotRecord[];
  parsed_count: number;
  existing_count: number;
  missing_projects: MissingProjectSummary[];
  new_projects?: AiSnapshotRecord[];
}

export interface AiRrMatchResponse {
  project_id: string;
  title?: string;
  status?: string;
  match_score: number | null;
  pros: string[];
  cons: string[];
  summary?: string;
  model_id?: string;
  scorecard?: {
    skills?: number | null;
    title?: number | null;
    experience?: number | null;
  };
  candidate_name?: string | null;
}

export interface PresignedUpload {
  bucket: string;
  key: string;
  upload_url: string;
  expires_in: number;
  max_size: number;
}

export interface ProjectReportSummaryRow {
  key: string;
  count: number;
  sample_titles?: string[];
  projects?: Array<{ project_id: string; title?: string; status?: string }>;
}

export interface ProjectReportsResponse {
  totals: {
    project_count: number;
    requested_hours_total: number;
    active_count: number;
    expired_count: number;
  };
  delivery_summary: ProjectReportSummaryRow[];
  status_summary: ProjectReportSummaryRow[];
  pipeline_summary: Record<string, number>;
  filters: Record<string, unknown>;
}

export interface AssignmentReportAssignment {
  assignment_id?: string;
  project_id?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  allocation?: string;
  requested_by?: string;
  approved_by?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AssignmentReportPerson {
  person_email: string;
  assignment_count: number;
  status_counts: Record<string, number>;
  assignments: AssignmentReportAssignment[];
  latest_activity?: string;
}

export interface AssignmentReportsResponse {
  totals: {
    person_count: number;
    assignment_count: number;
  };
  status_totals: Array<{ status: string; count: number }>;
  people: AssignmentReportPerson[];
}

export interface BeachSkill {
  skill: string;
  level: number;
}

export interface BeachRecord {
  employee_id: string;
  employee_name: string;
  employee_email: string;
  week_date: string;
  staffing_office: string;
  poh: string;
  role: string;
  grade: string;
  status?: StaffingStatus;
  potential_billable_effort?: number;
  weekly_beach_type: string;
  availability_status: string;
  days_unassigned?: number;
  days_unassigned_since_latest_ps?: number;
  accumulated_days_unassigned_12m?: number;
  accumulated_days_unassigned_ps_12m?: number;
  latest_project_type?: string;
  potential_billable_range?: string;
  is_leaving_beach?: string;
  days_to_leave_beach?: number;
  has_upcoming_long_leave?: boolean;
  has_future_assignment?: boolean;
  has_tentative_assignment?: boolean;
  is_leave_beach?: boolean;
  technical_skills?: BeachSkill[];
  testing_skills?: BeachSkill[];
  language_skills?: BeachSkill[];
  technical_skills_raw?: string;
  testing_skills_raw?: string;
  language_skills_raw?: string;
  created_at?: string;
  updated_at?: string;
}

export interface BeachDataResponse {
  items: BeachRecord[];
  count: number;
  filters_applied?: Record<string, string | undefined>;
}

export interface BeachUploadResponse {
  records_created: number;
  skill_indexes_created: number;
  total_records_attempted: number;
  filtered_count: number;
  total_rows_in_csv: number;
  errors: string[];
  success: boolean;
}

export interface BeachMatchCandidate {
  employee_email: string;
  matched_skills: string[];
  match_score: number;
  summary: string;
  beach_record: BeachRecord;
}

export interface BeachMatchResponse {
  project_id: string;
  week_date: string;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED" | "NOT_FOUND";
  matches: BeachMatchCandidate[];
  count: number;
  model_id?: string;
  message?: string;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
  execution_time_seconds?: number;
}

type RequestOptions = RequestInit & { query?: Record<string, string | undefined> };

type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider | null = null;

export function setAccessTokenProvider(provider: TokenProvider) {
  tokenProvider = provider;
}

export function clearAccessTokenProvider() {
  tokenProvider = null;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://localhost:3000";

interface ApiErrorInit {
  status: number;
  message: string;
  rawMessage?: string;
  details?: unknown;
}

export class ApiError extends Error {
  status: number;
  rawMessage?: string;
  details?: unknown;

  constructor({ status, message, rawMessage, details }: ApiErrorInit) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.rawMessage = rawMessage;
    this.details = details;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function buildUrl(path: string): URL {
  const base = API_BASE_URL.endsWith("/") ? API_BASE_URL : `${API_BASE_URL}/`;
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return new URL(normalizedPath, base);
}

async function request<T>(path: string, { query, ...init }: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path);
  if (query) {
    Object.entries(query)
      .filter(([, value]) => value != null && value !== "")
      .forEach(([key, value]) => url.searchParams.set(key, value as string));
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = tokenProvider ? await tokenProvider() : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      credentials: "omit",
      ...init,
      headers: {
        ...headers,
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError({
      status: 0,
      message: "We couldn't reach the staffing API. Check your network connection or VPN and try again.",
    });
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

async function buildApiError(response: Response): Promise<ApiError> {
  const bodyText = await response.text();
  let parsed: unknown;
  let rawMessage: string | undefined;

  if (bodyText) {
    try {
      parsed = JSON.parse(bodyText);
      if (parsed && typeof parsed === "object") {
        const record = parsed as Record<string, unknown>;
        rawMessage =
          firstNonEmpty(
            record["message"],
            record["error"],
            record["detail"],
            record["reason"],
            record["title"],
          ) ?? undefined;
      } else if (typeof parsed === "string") {
        rawMessage = parsed;
      }
    } catch {
      rawMessage = bodyText;
    }
  }

  const message = toFriendlyMessage(response.status, rawMessage);
  return new ApiError({
    status: response.status,
    message,
    rawMessage,
    details: parsed ?? (bodyText ? bodyText : undefined),
  });
}

function toFriendlyMessage(status: number, rawMessage?: string): string {
  const detail = normalizeDetail(rawMessage);
  const appendDetail = detail ? ` ${detail}` : "";

  if (status >= 500) {
    return `Something went wrong on the server.${appendDetail || " Please try again later."}`;
  }

  switch (status) {
    case 400:
      return `We couldn't process that request.${appendDetail || " Check the information and try again."}`;
    case 401:
      return `Your session appears to have expired.${appendDetail || " Sign in again to continue."}`;
    case 403:
      return `You don't have permission to complete that action.${appendDetail || " Request access if you believe this is a mistake."}`;
    case 404:
      return `We couldn't find what you were looking for.${appendDetail || " It may have been removed or renamed."}`;
    case 409:
      return `That action conflicts with an existing record.${appendDetail || " Refresh and confirm the latest data."}`;
    case 429:
      return `Too many requests in a short period.${appendDetail || " Please wait a moment and try again."}`;
    default:
      return `Request failed with status ${status}.${appendDetail ? appendDetail : ""}`;
  }
}

function normalizeDetail(detail?: string): string | null {
  if (!detail) {
    return null;
  }
  const trimmed = detail.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.endsWith(".") ? trimmed : `${trimmed}.`;
}

function firstNonEmpty(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}

export async function listPeople(filters?: { region?: string; status?: string; roles?: string; skills?: string }): Promise<Person[]> {
  const data = await request<{ items: Person[] }>("/people", { query: filters });
  return data.items;
}

export async function uploadCertifications(csv: string): Promise<{ created: number; updated: number; deleted: number; skipped: number }> {
  const data = await request<{ created: number; updated: number; deleted: number; skipped: number }>("/admin/certifications/upload", {
    method: "POST",
    body: JSON.stringify({ csv }),
  });
  return data;
}

export async function getPerson(email: string): Promise<Person> {
  return request<Person>(`/people/${encodeURIComponent(email)}`);
}

export async function createPerson(payload: Person): Promise<Person> {
  const data = await request<{ person: Person }>("/people", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.person;
}

export async function updatePerson(email: string, payload: Partial<Person>): Promise<Person> {
  const data = await request<{ person: Person }>(`/people/${encodeURIComponent(email)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.person;
}

export async function deletePerson(email: string): Promise<void> {
  await request(`/people/${encodeURIComponent(email)}`, {
    method: "DELETE",
  });
}

export async function listSkills(filters?: { group_type?: string; tag?: string }): Promise<Skill[]> {
  const data = await request<{ items: Skill[] }>("/skills", { query: filters });
  return data.items;
}

export async function createSkill(payload: Skill): Promise<Skill> {
  const data = await request<{ skill: Skill }>("/skills", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.skill;
}

export async function updateSkill(skillCode: string, payload: Partial<Skill>): Promise<Skill> {
  const data = await request<{ skill: Skill }>(`/skills/${encodeURIComponent(skillCode)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.skill;
}

export async function deleteSkill(skillCode: string): Promise<void> {
  await request(`/skills/${encodeURIComponent(skillCode)}`, {
    method: "DELETE",
  });
}

export async function listAssignments(filters?: { project_id?: string; person_email?: string; status?: string }): Promise<Assignment[]> {
  const data = await request<{ items: Assignment[] }>("/assignments", {
    query: filters,
  });
  return data.items;
}

export async function createAssignment(payload: Partial<Assignment> & { project_id: string; person_email: string; status: string }): Promise<Assignment> {
  const data = await request<{ assignment: Assignment }>("/assignments", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.assignment;
}

export async function updateAssignment(assignmentId: string, payload: Partial<Assignment>): Promise<Assignment> {
  const data = await request<{ assignment: Assignment }>(`/assignments/${encodeURIComponent(assignmentId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.assignment;
}

export async function deleteAssignment(assignmentId: string): Promise<void> {
  await request(`/assignments/${encodeURIComponent(assignmentId)}`, {
    method: "DELETE",
  });
}

export async function normalizeRequisitionText(text: string): Promise<AiNormalizedProject[]> {
  const data = await request<{ projects: AiNormalizedProject[] }>("/ai/rr-normalize", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  return data.projects ?? [];
}

export async function analyzeRequisitionSnapshot(text: string): Promise<AiSnapshotDiffResponse> {
  return request<AiSnapshotDiffResponse>("/ai/rr-diff", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

export async function matchResumeToProject(params: {
  projectId: string;
  resumeText?: string;
  fileBase64?: string;
  fileName?: string;
  s3Bucket?: string;
  s3Key?: string;
  notes?: string;
}): Promise<AiRrMatchResponse> {
  const payload: Record<string, unknown> = {
    project_id: params.projectId,
    resume_text: params.resumeText,
    notes: params.notes,
    file_base64: params.fileBase64,
    file_name: params.fileName,
    s3_bucket: params.s3Bucket,
    s3_key: params.s3Key,
  };
  return request<AiRrMatchResponse>("/ai/rr-match", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getRrMatchUploadUrl(params: { fileName: string; contentType?: string; fileSize?: number }): Promise<PresignedUpload> {
  const payload: Record<string, unknown> = {
    file_name: params.fileName,
    content_type: params.contentType,
    file_size: params.fileSize,
  };
  return request<PresignedUpload>("/ai/rr-match/upload", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchProjectReports(filters?: {
  includeExpired?: boolean;
  status?: string;
  startAfter?: string;
  startBefore?: string;
}): Promise<ProjectReportsResponse> {
  const query: Record<string, string> = {};
  if (filters?.includeExpired) query.include_expired = "true";
  if (filters?.status) query.status = filters.status;
  if (filters?.startAfter) query.start_after = filters.startAfter;
  if (filters?.startBefore) query.start_before = filters.startBefore;
  return request<ProjectReportsResponse>("/reports/projects", { query });
}

export function fetchAssignmentReports(): Promise<AssignmentReportsResponse> {
  return request<AssignmentReportsResponse>("/reports/assignments");
}

export async function createProject(payload: Project): Promise<Project> {
  const data = await request<{ project: Project }>("/projects", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.project;
}

export async function listProjects(filters?: { status?: string }): Promise<Project[]> {
  const data = await request<{ items: Project[] }>("/projects", {
    query: filters,
  });
  return data.items;
}

export async function updateProject(projectId: string, payload: Partial<Project>): Promise<Project> {
  const data = await request<{ project: Project }>(`/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.project;
}

export async function createOpportunity(payload: Partial<Opportunity>): Promise<Opportunity> {
  const data = await request<{ opportunity: Opportunity }>("/opportunities", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.opportunity;
}

export async function listOpportunities(): Promise<Opportunity[]> {
  const data = await request<{ items: Opportunity[] }>("/opportunities");
  return data.items;
}

export async function getOpportunity(opportunityId: string): Promise<Opportunity> {
  return request<Opportunity>(`/opportunities/${encodeURIComponent(opportunityId)}`);
}

export async function updateOpportunity(opportunityId: string, payload: Partial<Opportunity>): Promise<Opportunity> {
  const data = await request<{ opportunity: Opportunity }>(`/opportunities/${encodeURIComponent(opportunityId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.opportunity;
}

export async function deleteOpportunity(opportunityId: string): Promise<void> {
  await request<{ message: string }>(`/opportunities/${encodeURIComponent(opportunityId)}`, {
    method: "DELETE",
  });
}

export async function getFeatureFlags(): Promise<FeatureFlags> {
  const data = await request<{ flags: FeatureFlags }>("/feature-flags");
  return data.flags;
}

export async function updateFeatureFlags(flags: Partial<FeatureFlags>): Promise<FeatureFlags> {
  const data = await request<{ flags: FeatureFlags }>("/feature-flags", {
    method: "PUT",
    body: JSON.stringify({ flags }),
  });
  return data.flags;
}

export async function listRoles(): Promise<RoleDefinition[]> {
  const data = await request<{ roles: RoleDefinition[] }>("/lookups/roles");
  return data.roles;
}

export async function listArchetypes(): Promise<Archetype[]> {
  const data = await request<{ archetypes: Archetype[] }>("/lookups/archetypes");
  return data.archetypes;
}

export async function updatePersonSkills(email: string, skills: string[]): Promise<Person> {
  return updatePerson(email, { skills });
}

// Beach Data API

export async function listBeachWeeks(): Promise<{ weeks: string[]; count: number }> {
  return request<{ weeks: string[]; count: number }>("/beach?list_weeks=true", {
    method: "GET",
  });
}

export async function uploadBeachData(csv: string, weekDate: string): Promise<BeachUploadResponse> {
  return request<BeachUploadResponse>("/beach/upload", {
    method: "POST",
    body: JSON.stringify({ csv, week_date: weekDate }),
  });
}

export async function listBeachData(filters?: {
  week?: string;
  email?: string;
  office?: string;
  role?: string;
  availability?: string;
  skill?: string;
  skill_level?: string;
}): Promise<BeachDataResponse> {
  const query: Record<string, string> = {};
  if (filters?.week) query.week = filters.week;
  if (filters?.email) query.email = filters.email;
  if (filters?.office) query.office = filters.office;
  if (filters?.role) query.role = filters.role;
  if (filters?.availability) query.availability = filters.availability;
  if (filters?.skill) query.skill = filters.skill;
  if (filters?.skill_level) query.skill_level = filters.skill_level;
  return request<BeachDataResponse>("/beach", { query });
}

export async function getBeachRecord(employeeId: string, week: string): Promise<BeachRecord> {
  return request<BeachRecord>(`/beach/${employeeId}/${week}`);
}

export async function updateBeachRecord(
  employeeId: string,
  week: string,
  updates: Partial<BeachRecord>
): Promise<BeachRecord> {
  return request<BeachRecord>(`/beach/${employeeId}/${week}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

export async function deleteBeachRecord(employeeId: string, week: string): Promise<void> {
  await request<void>(`/beach/${employeeId}/${week}`, {
    method: "DELETE",
  });
}

/**
 * Trigger async beach candidate matching for a project.
 * Returns immediately with status PENDING. Results can be fetched with getBeachMatchResults.
 */
export async function triggerBeachMatch(projectId: string, weekDate?: string): Promise<{message: string; project_id: string; status: string}> {
  return request<{message: string; project_id: string; status: string}>("/ai/beach-match/trigger", {
    method: "POST",
    body: JSON.stringify({
      project_id: projectId,
      week_date: weekDate,
    }),
  });
}

/**
 * Get beach candidate matching results for a project.
 * Returns the current status and results (if completed).
 */
export async function getBeachMatchResults(projectId: string): Promise<BeachMatchResponse> {
  return request<BeachMatchResponse>(`/ai/beach-match/${projectId}`, {
    method: "GET",
  });
}

/**
 * Admin: Reset/clear beach match status for a project.
 * Removes the beach match record from DynamoDB.
 */
export async function resetBeachMatchStatus(projectId: string): Promise<{message: string; project_id: string; previous_status: string}> {
  return request<{message: string; project_id: string; previous_status: string}>("/admin/beach-match/reset", {
    method: "POST",
    body: JSON.stringify({
      project_id: projectId,
    }),
  });
}
